import React, { useEffect, useRef } from 'react';

/**
 * The shell both modals sit in.
 *
 * Neither was a dialog: no role, no `aria-modal`, no Escape, no focus handling anywhere in the
 * file. A keyboard user opening the credit-terms editor kept focus on the button behind the
 * overlay, could Tab into the obscured table underneath, and had no way out except finding Cancel;
 * a screen reader was given no dialog boundary at all and read the page behind it as still present.
 *
 * Focus is moved in on open and RESTORED on close, because losing your place in a long customer
 * table is the part people actually notice.
 */
/** Everything inside the panel a keyboard can land on. The panel itself is tabIndex -1, so it is
 *  excluded by the last selector rather than by a special case. */
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), ' +
  'select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function Modal({
  labelledBy,
  onClose,
  closeDisabled = false,
  children,
  className = '',
}) {
  const panel = useRef(null);
  const returnTo = useRef(null);
  /**
   * Read through refs, and the effect below runs ONCE.
   *
   * With `onClose` and `closeDisabled` as dependencies, every flip of `saving` tore the effect down
   * and rebuilt it - and the teardown is what restores focus to whoever opened the dialog. So
   * pressing Save moved focus out of the dialog and then snatched it back to the panel, out from
   * under the textarea, twice per save. The values still have to be current when a key is pressed,
   * which is what these are for.
   */
  const closeRef = useRef(onClose);
  const disabledRef = useRef(closeDisabled);
  useEffect(() => {
    closeRef.current = onClose;
    disabledRef.current = closeDisabled;
  });

  useEffect(() => {
    returnTo.current = document.activeElement;
    // The panel itself takes focus rather than a guessed first control: which element should be
    // focused differs between the two modals, and a wrong guess is worse than a predictable start.
    panel.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (disabledRef.current) return;
        e.stopPropagation();
        closeRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      /**
       * THE TRAP ITSELF.
       *
       * `aria-modal` tells a screen reader that the rest of the page is not there. Nothing was
       * telling the keyboard, and the page behind an overlay is still rendered and still focusable -
       * so Tab walked out of the credit-terms editor and into the customer table underneath it,
       * completely obscured, with the operator typing into something they could not see.
       */
      const root = panel.current;
      if (!root) return;
      const focusable = Array.from(root.querySelectorAll(FOCUSABLE));
      if (focusable.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (!root.contains(active) || active === root) {
        // `active === root` is the state every dialog OPENS in - focus is put on the panel itself,
        // which is neither `first` nor `last`, so without this clause the very first Shift+Tab fell
        // through every branch and walked natively out of the overlay into the obscured table. One
        // keystroke, from the state the dialog always starts in.
        //
        // The other case is focus escaping some other way - a click on the page behind, or a
        // programmatic move. Bring it back to the edge it was heading for rather than leaving it.
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const back = returnTo.current;
      if (back && typeof back.focus === 'function' && document.contains(back)) back.focus();
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        className={`bg-white rounded-xl w-full outline-none ${className}`}
      >
        {children}
      </div>
    </div>
  );
}
